import { useMemo, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import Turnstile from "react-turnstile";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { MessageSquare } from "lucide-react";
import { postFeedback } from "@/lib/backend";

interface Props {
  recentPrompts: string[];
}

export default function FeedbackDialog({ recentPrompts }: Props) {
  const [comment, setComment] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const { toast } = useToast();
  const { user, isLoaded, isSignedIn } = useUser();
  const userHeaders = useMemo(() => {
    if (!isLoaded || !isSignedIn) return undefined;
    const id = user?.id;
    if (!id) return undefined;
    return {
      id,
      email: user?.primaryEmailAddress?.emailAddress || undefined,
    };
  }, [isLoaded, isSignedIn, user?.id, user?.primaryEmailAddress?.emailAddress]);

  const handleSubmit = async () => {
    if (!comment.trim()) return;
    setIsSending(true);
    try {
      const result = await postFeedback(
        {
          message: comment.trim(),
          lastChats: (recentPrompts || []).slice(-3).map((p) => ({ role: "user", content: p })),
          captchaToken: captchaToken || "",
          email: userHeaders?.email,
        },
        userHeaders,
      );

      if (result?.ok) {
        toast({ title: "Feedback sent — thank you!" });
        setComment("");
        setCaptchaToken(null);
      } else {
        toast({
          title: "Error sending feedback",
          description: result?.error || result?.body || "Unable to send feedback.",
          variant: "destructive",
        });
      }
    } catch (e: any) {
      toast({ title: "Network error", description: e?.message || "Unable to send" , variant: "destructive"});
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          className="flex h-8 items-center justify-center gap-1 rounded-full border border-border bg-calcera-purple px-3 hover:bg-primary/90 focus:outline-none"
          aria-label="Send feedback"
        >
          <MessageSquare className="w-4 h-4 text-primary-foreground/90" />
          <span className="text-xs font-semibold text-primary-foreground/90">Feedback</span>
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Please feel free to give us feedback</DialogTitle>
          <DialogDescription>Tell us what you think:</DialogDescription>
        </DialogHeader>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Your comments..."
          rows={5}
        />
        {import.meta.env.VITE_TURNSTILE_SITE_KEY ? (
          <div className="mt-2">
            <Turnstile
              sitekey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
              onVerify={(token) => setCaptchaToken(token)}
            />
          </div>
        ) : null}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary">Cancel</Button>
          </DialogClose>
          <Button disabled={isSending || !comment.trim()} onClick={handleSubmit}>
            {isSending ? "Sending..." : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
